import { ACCESS_LEVELS, ASSET_FLAGS, BANDS, CELLS, CONTROL_LOSS_REASONS, DAMAGE_CLASSES, EFFECT_SOURCES, ESCALATION_LEVELS, GAME_EVENT_TYPES, HARDENINGS, INTEL_CATEGORIES, MISSION_IDS, MOBILITIES, MW_CATEGORIES, MW_DISPOSITIONS, MW_IR_INTENSITIES, OUTCOMES, SEGMENTS, SIGNATURES, STATUSES, WEATHER_STATES, } from '@badger-sentinel/rules';
import { z } from 'zod';
import { zBand, zCell, zConsoleTab, zMwObservableField, zMwRevealCursor, zMwStage, zStatus, zTeamId, } from './enums.js';
/**
 * Wire schemas for the projection (§6).
 *
 * ## The type/schema split (decision M2-D3)
 *
 * `packages/rules` owns the `CellView` *type* — the projection is pure and lives
 * with the engine. This package owns the *schema*, because §19 makes it the only
 * definition of any wire format and `packages/rules` may not depend on Zod.
 *
 * Neither rule gives, so the two are held together by a bidirectional
 * type-equivalence assertion in `test/view-contract.test.ts`, which runs under
 * `pnpm typecheck`. Adding a field to `CellView` without adding it here does not
 * compile.
 *
 * ## Strictness (decision M2-D5)
 *
 * Every object here is `.strict()`, and the server parses each outbound frame
 * against these schemas before it writes to the socket. An accidental extra field
 * — a debugging addition, a spread of the wrong object, the residue of a
 * clone-and-delete — becomes a loud 500 instead of a quiet leak.
 *
 * ## Readonly
 *
 * The schemas are `.readonly()` to mirror the immutability of the domain types.
 * That costs nothing on the wire (JSON has no such concept) and is what lets the
 * equivalence assertion be an equality rather than a one-way check.
 */
const strict = (shape) => z.object(shape).strict();
// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------
/** `CTC-3F9A2C81` — an opaque, per-observer handle (decision M2-D2). */
export const zContactAlias = z.string().regex(/^CTC-[0-9A-F]{8}$/, 'Not a contact handle');
export const zContactLocationView = z.discriminatedUnion('kind', [
    strict({ kind: z.literal('BAND'), band: zBand }),
    strict({ kind: z.literal('SLOT'), band: zBand, slot: z.number().int().positive() }),
    strict({ kind: z.literal('REGION'), region: z.string() }),
    strict({ kind: z.literal('UNKNOWN') }),
]);
export const zContactStatusView = strict({
    status: zStatus,
    certain: z.boolean(),
    asOfTurn: z.number().int(),
}).readonly();
export const zContactView = strict({
    id: zContactAlias,
    // Never HIDDEN — a hidden contact is absent from the projection entirely.
    rung: z.enum(['FOUND', 'FIXED', 'TRACKED']),
    characterized: z.boolean(),
    presentedType: z.string(),
    presentedTypeName: z.string(),
    location: zContactLocationView,
    expiresTurn: z.number().int().nullable(),
    publishedBy: zCell.nullable(),
    believedStatus: zContactStatusView.nullable(),
}).readonly();
// ---------------------------------------------------------------------------
// Friendly forces
// ---------------------------------------------------------------------------
const zAssetLocation = z.discriminatedUnion('kind', [
    strict({ kind: z.literal('ORBIT'), band: zBand, slot: z.number().int() }),
    strict({
        kind: z.literal('TRANSIT'),
        from: zBand,
        to: zBand,
        destSlot: z.number().int(),
        arrivesTurn: z.number().int(),
    }),
    strict({ kind: z.literal('GROUND'), region: z.string() }),
    strict({ kind: z.literal('OFFMAP') }),
]);
export const zStatusEffectView = strict({
    severity: z.enum(['DEGRADED', 'OFFLINE']),
    /**
     * `'UNKNOWN'` until attribution (§6.3). Typed as a plain string rather than an
     * enum of the sources plus `'UNKNOWN'`, so that a client cannot enumerate the
     * source vocabulary out of the schema it was shipped.
     */
    cause: z.string(),
    attributedTo: zTeamId.nullable(),
    expiresAtTurn: z.number().int().nullable(),
    permanent: z.boolean(),
}).readonly();
export const zFriendlyAssetView = strict({
    id: z.string(),
    typeId: z.string(),
    typeName: z.string(),
    assignedCell: zCell,
    orderable: z.boolean(),
    segment: z.enum(SEGMENTS),
    location: zAssetLocation,
    status: zStatus,
    effects: z.array(zStatusEffectView).readonly(),
    controllable: z.boolean(),
    controlLoss: z.enum(CONTROL_LOSS_REASONS).nullable(),
    deltaV: z.number().nullable(),
    capacity: z.number().int(),
    missionsThisTurn: z.array(z.enum(MISSION_IDS)).readonly(),
    magazine: z.number().int().nullable(),
    ttcStation: z.string().nullable(),
    ttcCapacity: z.number().int().nullable(),
    network: z.string(),
    signature: z.enum(SIGNATURES),
    hardening: z.enum(HARDENINGS),
    mobility: z.enum(MOBILITIES).nullable(),
    damageClass: z.enum(DAMAGE_CLASSES).nullable(),
    flags: z.array(z.enum(ASSET_FLAGS)).readonly(),
    missions: z.array(z.enum(MISSION_IDS)).readonly(),
    relocate: strict({ toRegion: z.string(), completesTurn: z.number().int() }).readonly().nullable(),
    emcon: z.boolean(),
    deployed: z.boolean().nullable(),
    deployParent: z.string().nullable(),
    deployCapacity: z.number().int().nullable(),
    onMission: z.boolean(),
}).readonly();
// ---------------------------------------------------------------------------
// Theatre and map
// ---------------------------------------------------------------------------
export const zEscalationEntryView = strict({
    turn: z.number().int(),
    action: z.string(),
    finalValue: z.number().int(),
    applied: z.boolean(),
}).readonly();
const zBandRecord = (value) => strict(Object.fromEntries(BANDS.map((b) => [b, value])));
export const zTheatreView = strict({
    escalationCounter: z.number().int(),
    escalationLevel: z.enum(ESCALATION_LEVELS),
    ownContributions: z.array(zEscalationEntryView).readonly(),
    ownRoeViolations: z.number().int(),
    debris: zBandRecord(z.number().int()).readonly(),
    weather: z.record(z.string(), z.enum(WEATHER_STATES)).readonly(),
    forecast: z.record(z.string(), z.enum(WEATHER_STATES)).readonly().nullable(),
}).readonly();
export const zRoeRuleView = strict({
    mission: z.enum(MISSION_IDS),
    weaponType: z.string().nullable(),
    payload: z.string().nullable(),
    forbidden: z.boolean(),
    requiresEscalationAtLeast: z.number().int().nullable(),
    hardBlock: z.boolean(),
    note: z.string().nullable(),
    violatedNow: z.boolean(),
}).readonly();
export const zRegionView = strict({
    id: z.string(),
    name: z.string(),
    adjacentTo: z.array(z.string()).readonly(),
}).readonly();
export const zRegionRangeView = strict({
    from: z.string(),
    to: z.string(),
    range: z.number().int().nonnegative(),
}).readonly();
export const zMapView = strict({
    regions: z.array(zRegionView).readonly(),
    ranges: z.array(zRegionRangeView).readonly(),
    bandSlots: zBandRecord(z.number().int()).readonly(),
}).readonly();
// ---------------------------------------------------------------------------
// Reserved for M3/M4 — present and empty, so the shape never churns
// ---------------------------------------------------------------------------
/**
 * `stageModifier`, never `interceptModifier` (decision M4-D7). The total is computed
 * against the launch's true model, so sending it would tell Joint Fires whether
 * Missile Warning got the call right before they commit a missile.
 */
export const zMwReleaseView = strict({
    eventId: z.string(),
    releasedAtStage: z.number().int(),
    assignedIdentity: z.string(),
    stageModifier: z.number().int(),
    interceptable: z.boolean(),
}).readonly();
export const zMwObservation = strict({
    stage: zMwStage,
    field: zMwObservableField,
    /** `null` ⇒ a DEGRADED constellation dropped it. Present, and empty. */
    value: z.string().nullable(),
}).readonly();
export const zMwTrackView = strict({
    eventId: z.string(),
    firstSeenTurn: z.number().int(),
    revealedThroughStage: zMwRevealCursor,
    observations: z.array(zMwObservation).readonly(),
    workingIdentity: z.string().nullable(),
    release: strict({
        stage: zMwStage,
        identity: z.string(),
        disposition: z.enum(MW_DISPOSITIONS),
    })
        .readonly()
        .nullable(),
    briefed: z.boolean(),
    open: z.boolean(),
    stageSchedule: z
        .array(strict({ stage: z.number().int(), atMs: z.number().int() }).readonly())
        .readonly(),
}).readonly();
/**
 * The prose columns and the numeric ones together (M5 F3). The prose is what the
 * recognition table displays; `apogeeKm` and `rv` are what its filter engine
 * compares. `apogeeKm: null` is a row with no ballistic apogee, and is not a zero.
 */
export const zMwModelView = strict({
    id: z.string(),
    displayName: z.string(),
    category: z.enum(MW_CATEGORIES),
    irIntensity: z.enum(MW_IR_INTENSITIES),
    boostDurationS: z.tuple([z.number(), z.number()]).readonly(),
    stagingEvents: z.number().int(),
    apogee: z.string(),
    groundTrack: z.string(),
    terminal: z.string(),
    apogeeKm: z.tuple([z.number(), z.number()]).readonly().nullable(),
    rv: z.tuple([z.number(), z.number()]).readonly(),
}).readonly();
export const zInFlightWeaponView = strict({
    id: z.string(),
    weaponType: z.string(),
    target: z.string(),
    arrivesTurn: z.number().int(),
    custodyMaintained: z.boolean(),
}).readonly();
export const zCyberContactView = strict({
    alias: z.string(),
    network: z.string(),
    access: z.enum(ACCESS_LEVELS),
    hardening: z.enum(HARDENINGS).nullable(),
    persistence: z.boolean(),
}).readonly();
/**
 * An enclave, with its way in (M5 F4).
 *
 * `router` is a plain string rather than `zContactAlias` because it is one of two
 * things depending on side — an own asset id, or the viewer's alias for an enemy
 * machine — and a schema that admitted only the alias form would reject a team's
 * view of its own network. The projection is what decides which; see
 * `cyberNetworkViews`.
 */
export const zCyberNetworkView = strict({
    id: z.string(),
    name: z.string(),
    side: zTeamId,
    router: z.string().nullable(),
    hardening: z.enum(HARDENINGS).nullable(),
}).readonly();
export const zCyberView = strict({
    footholds: z.array(z.string()).readonly(),
    contacts: z.array(zCyberContactView).readonly(),
    friendlyIsolated: z.array(z.string()).readonly(),
    friendlyHardening: z
        .array(strict({ asset: z.string(), hardening: z.enum(HARDENINGS) }).readonly())
        .readonly(),
    networks: z.array(zCyberNetworkView).readonly(),
}).readonly();
export const zObjectiveView = strict({
    id: z.string(),
    kind: z.string(),
    description: z.string(),
    points: z.number().int(),
    progress: z.number().nullable(),
}).readonly();
// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------
export const zViewerRef = strict({ team: zTeamId, cell: zCell }).readonly();
export const zSeatCapabilitiesView = strict({
    readOnly: z.boolean(),
    canPublish: z.boolean(),
    seesUnpublishedContacts: z.boolean(),
    seesTeamDrafts: z.boolean(),
    /** Which console modules this seat sees (M5 Q15) — presentation, not permission. */
    tabs: z.array(zConsoleTab).readonly(),
}).readonly();
export const zCellView = strict({
    kind: z.literal('CELL'),
    turn: z.number().int(),
    viewer: zViewerRef,
    capabilities: zSeatCapabilitiesView,
    friendly: z.array(zFriendlyAssetView).readonly(),
    contacts: z.array(zContactView).readonly(),
    theatre: zTheatreView,
    map: zMapView,
    mwReleases: z.array(zMwReleaseView).readonly(),
    mwTracks: z.array(zMwTrackView).readonly(),
    mwCatalogue: z.array(zMwModelView).readonly(),
    inFlight: z.array(zInFlightWeaponView).readonly(),
    cyber: zCyberView,
    roe: z.array(zRoeRuleView).readonly(),
    objectives: z.array(zObjectiveView).readonly(),
}).readonly();
// ---------------------------------------------------------------------------
// Intel reports
// ---------------------------------------------------------------------------
export const zIntelEntry = strict({
    seq: z.number().int(),
    phase: z.number().int(),
    category: z.enum(INTEL_CATEGORIES),
    type: z.enum(GAME_EVENT_TYPES),
    outcome: z.enum(OUTCOMES).nullable(),
    actor: z.string().nullable(),
    contact: z.string().nullable(),
    /**
     * Scalars only. A nested object here would be a place for an untranslated asset
     * id to hide, which is why `buildIntelReport` drops them rather than walking
     * them (M2 §5.5).
     */
    fields: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).readonly(),
}).readonly();
export const zIntelReport = strict({
    turn: z.number().int(),
    viewer: zViewerRef,
    entries: z.array(zIntelEntry).readonly(),
}).readonly();
// ---------------------------------------------------------------------------
// GM view
// ---------------------------------------------------------------------------
export const zGmAssetView = strict({
    id: z.string(),
    owner: zTeamId,
    typeId: z.string(),
    typeName: z.string(),
    assignedCell: zCell,
    location: zAssetLocation,
    status: zStatus,
    destroyed: z.boolean(),
    deltaV: z.number().nullable(),
    magazine: z.number().int().nullable(),
    hardening: z.string(),
    network: z.string(),
    ttcStation: z.string().nullable(),
    controllable: z.boolean(),
    controlLoss: z.enum(CONTROL_LOSS_REASONS).nullable(),
    flags: z.array(z.string()).readonly(),
    disguisedAs: z.string().nullable(),
    vpValue: z.number().int(),
}).readonly();
export const zGmEffectView = strict({
    id: z.string(),
    target: z.string(),
    severity: z.enum(['DEGRADED', 'OFFLINE']),
    source: z.enum(EFFECT_SOURCES),
    attributedTo: zTeamId.nullable(),
    expiresAtTurn: z.number().int().nullable(),
    sustained: z.boolean(),
    sustainedBy: z.string().nullable(),
    permanent: z.boolean(),
}).readonly();
export const zGmBeliefView = strict({
    observer: zTeamId,
    subject: z.string(),
    alias: z.string(),
    rung: z.enum(['HIDDEN', 'FOUND', 'FIXED', 'TRACKED']),
    characterized: z.boolean(),
    believedBand: zBand.nullable(),
    believedSlot: z.number().int().nullable(),
    believedRegion: z.string().nullable(),
    actualBand: zBand.nullable(),
    actualSlot: z.number().int().nullable(),
    actualRegion: z.string().nullable(),
    divergent: z.boolean(),
    publishedBy: z.array(zCell).readonly(),
}).readonly();
export const zGmPhantomView = strict({
    id: z.string(),
    alias: z.string(),
    believedBy: zTeamId,
    presentedType: z.string(),
    rung: z.enum(['HIDDEN', 'FOUND', 'FIXED', 'TRACKED']),
    origin: z.string(),
    createdTurn: z.number().int(),
    expiresTurn: z.number().int(),
    location: z.union([
        strict({ band: zBand, slot: z.number().int() }).readonly(),
        strict({ region: z.string() }).readonly(),
    ]),
}).readonly();
export const zScenarioDefect = strict({
    kind: z.enum(['TTC_CYCLE', 'TTC_OVER_CAPACITY', 'TTC_NOT_A_STATION', 'TTC_MISSING']),
    assets: z.array(z.string()).readonly(),
    detail: z.string(),
}).readonly();
export const zGmCyberView = strict({
    attacker: zTeamId,
    subject: z.string(),
    kind: z.enum(['FOOTHOLD', 'ACCESS']),
    level: z.string(),
    persistence: z.boolean(),
}).readonly();
export const zGmWeaponView = strict({
    id: z.string(),
    owner: zTeamId,
    launcher: z.string(),
    weaponType: z.string(),
    target: z.string(),
    arrivesTurn: z.number().int(),
    custodyMaintained: z.boolean(),
}).readonly();
export const zGmMwEventView = strict({
    id: z.string(),
    warnedTeam: zTeamId,
    launchTurn: z.number().int(),
    trueType: z.string(),
    trueModel: z.string(),
    benign: z.boolean(),
    revealedThroughStage: zMwRevealCursor,
    omittedFields: z.array(z.string()).readonly(),
    received: z.boolean(),
    briefed: z.boolean(),
    workingIdentity: z.string().nullable(),
    releasedAtStage: z.number().int().nullable(),
    assignedIdentity: z.string().nullable(),
    disposition: z.string().nullable(),
}).readonly();
export const zGmEscalationEntryView = strict({
    id: z.string(),
    turn: z.number().int(),
    causedBy: zTeamId,
    action: z.string(),
    baseValue: z.number().int(),
    finalValue: z.number().int(),
    applied: z.boolean(),
    attributionRequired: z.boolean(),
    attributedTurn: z.number().int().nullable(),
    multipliers: z.array(strict({ source: z.string(), factor: z.number() }).readonly()).readonly(),
}).readonly();
const zTeamRecord = (value) => strict({ BLUFOR: value, REDFOR: value });
export const zGmView = strict({
    kind: z.literal('GM'),
    turn: z.number().int(),
    assets: z.array(zGmAssetView).readonly(),
    effects: z.array(zGmEffectView).readonly(),
    beliefs: z.array(zGmBeliefView).readonly(),
    phantoms: z.array(zGmPhantomView).readonly(),
    theatre: strict({
        escalationCounter: z.number().int(),
        escalationLevel: z.string(),
        debris: zBandRecord(z.number().int()).readonly(),
        weather: z.record(z.string(), z.enum(WEATHER_STATES)).readonly(),
        forecast: z.record(z.string(), z.enum(WEATHER_STATES)).readonly(),
        roeViolations: zTeamRecord(z.number().int()).readonly(),
    }).readonly(),
    cyber: z.array(zGmCyberView).readonly(),
    weapons: z.array(zGmWeaponView).readonly(),
    mwEvents: z.array(zGmMwEventView).readonly(),
    escalation: z.array(zGmEscalationEntryView).readonly(),
    map: zMapView,
    defects: z.array(zScenarioDefect).readonly(),
}).readonly();
/** Every outbound view schema, for the strictness sweep in `strictness.test.ts`. */
export const OUTBOUND_VIEW_SCHEMAS = {
    zContactView,
    zStatusEffectView,
    zFriendlyAssetView,
    zTheatreView,
    zMapView,
    zCellView,
    zRoeRuleView,
    zCyberView,
    zCyberContactView,
    zCyberNetworkView,
    zSeatCapabilitiesView,
    zInFlightWeaponView,
    zMwReleaseView,
    zMwObservation,
    zMwTrackView,
    zMwModelView,
    zIntelEntry,
    zIntelReport,
    zGmAssetView,
    zGmEffectView,
    zGmCyberView,
    zGmWeaponView,
    zGmMwEventView,
    zGmEscalationEntryView,
    zGmView,
};
export { CELLS, STATUSES };
//# sourceMappingURL=view.js.map